import React, { Component, MouseEvent } from "react";
import DataGrid, { Column, FilterPanel, FilterRow, GroupPanel, HeaderFilter, Pager, Paging, Scrolling, Search, SearchPanel } from "devextreme-react/data-grid";
import { LoadPanel, Popup, ScrollView } from "devextreme-react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUser } from "@fortawesome/free-solid-svg-icons";
import LightModePageTemplate from "../page/Templates/LightModeTemplate";
import IDChecksService, { IdCheckRow, ID_CHECK_NO_SERVICES_FILTER, getIdCheckServices } from "../../services/IDChecksService";
import ArtistProfileComponent from "../Artist-Profile/ArtistProfileComponent";
import ProfileComponentTabs from "../Artist-Profile/ProfileComponentTabs";
import GridUtils from "../grid/GridUtilities";
import { confirm } from "devextreme/ui/dialog";

interface IDChecksGridProps {
    statusFilter: string;
    serviceTypeId: string;
     
    serviceFilter: string;
    onServiceOptionsLoaded?: (services: string[]) => void;
    onStatusOptionsLoaded?: (statuses: string[]) => void;
}

interface IDChecksGridState {
    dataSource: IdCheckRow[];
    loadIndicatorForGrid: boolean;
    popupVisible: boolean;
    selectedBusinessEntityId: string;
}

class IDChecksGrid extends Component<IDChecksGridProps> {
    state: IDChecksGridState;
    idChecksService: IDChecksService;
    gridUtils: GridUtils;

    constructor(props: IDChecksGridProps) {
        super(props);
        this.idChecksService = new IDChecksService();
        this.gridUtils = new GridUtils();
        this.state = {
            dataSource: [],
            loadIndicatorForGrid: false,
            popupVisible: false,
            selectedBusinessEntityId: "",
        };
    }

     
    /**
     * Fetches ID check records from the API and stores them in component state.
     */
    fetchIdChecks = () => {
        this.idChecksService
            .getIdChecks()
            .then((response) => {
                const data = response.data?.data ?? response.data ?? [];
                const rows: IdCheckRow[] = Array.isArray(data) ? data : [];
                this.setState({
                    dataSource: rows,
                    loadIndicatorForGrid: false,
                });
                this.publishServiceOptions(rows);
                this.publishStatusOptions(rows);
            })
            .catch((error) => {
                this.setState({ loadIndicatorForGrid: false });
                const message = "IDChecks data load failed: " + JSON.stringify(error);
                if (!this.idChecksService.traceAsErrorToAppInsights(message)) {
                    console.error(message);
                }
            });
    };

     
    /**
     * Sends the distinct, sorted service names found in the loaded rows to the parent
     * so the side panel can offer them as local filter options.
     */
    publishServiceOptions = (rows: IdCheckRow[]) => {
        if (!this.props.onServiceOptionsLoaded) {
            return;
        }
        const distinct = new Set<string>();
        rows.forEach((row) => getIdCheckServices(row).forEach((service) => distinct.add(service)));
        this.props.onServiceOptionsLoaded(Array.from(distinct).sort((a, b) => a.localeCompare(b)));
    };

     
    /**
     * Sends the distinct, sorted statuses found in the loaded rows to the parent
     * so the side panel can offer them as local filter options.
     */
    publishStatusOptions = (rows: IdCheckRow[]) => {
        if (!this.props.onStatusOptionsLoaded) {
            return;
        }
        const distinct = new Set<string>();
        rows.forEach((row) => {
            const status = String(row.status ?? "").trim();
            if (status) {
                distinct.add(status);
            }
        });
        this.props.onStatusOptionsLoaded(Array.from(distinct).sort((a, b) => a.localeCompare(b)));
    };

     
    /**
     * Applies the local status and services filters to the loaded rows. Frontend only — no API call.
     */
    getFilteredDataSource = (): IdCheckRow[] => {
        const { serviceFilter, statusFilter } = this.props;
        let rows = this.state.dataSource;
        if (statusFilter) {
            const targetStatus = statusFilter.toLowerCase();
            rows = rows.filter((row) => String(row.status ?? "").trim().toLowerCase() === targetStatus);
        }
        if (!serviceFilter) {
            return rows;
        }
        if (serviceFilter === ID_CHECK_NO_SERVICES_FILTER) {
            return rows.filter((row) => getIdCheckServices(row).length === 0);
        }
        const target = serviceFilter.toLowerCase();
        return rows.filter((row) =>
            getIdCheckServices(row).some((service) => service.toLowerCase() === target)
        );
    };

    componentDidMount() {
        this.loadDataSource();
    }

    componentDidUpdate(prevProps: IDChecksGridProps) {
        if (prevProps.serviceTypeId !== this.props.serviceTypeId) {
            this.loadDataSource();
        }
    }

     
    /**
     * Triggers a data reload with the current filter props.
     */
    loadDataSource = () => {
        this.setState({ loadIndicatorForGrid: true }, this.fetchIdChecks);
    };

     
    /**
     * Displays provider profile icon column. Clicking opens the Artist Details popup
     * for the row's associated provider.
     */
    profileDisplay = (cellInfo: any) => {
        const businessEntityId: string = cellInfo?.data?.providerId || "";
        return (
            <div className="d-flex justify-content-between align-items-center">
                <button
                    className={"btn icon-btn"}
                    aria-label="View artist details"
                    onClick={(e: MouseEvent) => {
                        e.preventDefault();
                        this.setState({
                            popupVisible: true,
                            selectedBusinessEntityId: String(businessEntityId),
                        });
                    }}
                >
                    <FontAwesomeIcon icon={faUser} />
                </button>
            </div>
        );
    };

    /**
     * Handles the Popup onHiding event. Cancels the native DevExtreme hide and shows
     * a confirm dialog — matching the pattern used in ArtistGrid and SignUpProfileUpdateGrid
     * to prevent the ResizeObserver loop notification.
     */
    hideProfilePopup = (e: any) => {
        e.cancel = true;
        confirm("<div class='text-center'>Close screen without saving?</div>", "Close").then((hasConfirmed) => {
            if (hasConfirmed) {
                this.setState({ popupVisible: false, selectedBusinessEntityId: "" });
            }
        });
    };

    /**
     * Closes the popup directly.
     */
    hideProfileTab = () => {
        this.setState({ popupVisible: false, selectedBusinessEntityId: "" });
    };

    handlePopUpClose = (flag: boolean) => {
        this.setState({
            popupVisible: flag,
            selectedBusinessEntityId: flag ? this.state.selectedBusinessEntityId : "",
        });
    };

    render() {
        return (
            <>
                <LightModePageTemplate>
                    <DataGrid
                        elementAttr={{ id: "gridContainer" }}
                        dataSource={this.getFilteredDataSource()}
                        showBorders={false}
                        showColumnLines={false}
                        allowColumnReordering={true}
                        allowColumnResizing={true}
                        columnAutoWidth={true}
                        columnResizingMode={"widget"}
                        hoverStateEnabled={true}
                        noDataText={"No ID checks found for the filters used"}
                        columnMinWidth={120}
                    >
                        <Paging defaultPageSize={10} />
                        <Pager showPageSizeSelector={true} allowedPageSizes={[5, 10, 20]} showInfo={true} />
                        <HeaderFilter visible={true}>
                            <Search enabled={true} />
                        </HeaderFilter>
                        <FilterRow visible={true} applyFilter="auto" />
                        <GroupPanel visible={true} />
                        <FilterPanel visible={true} />
                        <Scrolling useNative={true} showScrollbar={"always"} />
                        <SearchPanel visible={true} placeholder={"Search"} />

                        <Column caption="PROFILE" allowExporting={false} cellRender={this.profileDisplay} />
                        <Column dataField="status" caption="STATUS" />
                        <Column dataField="providerId" caption="PROVIDER ID" />
                        <Column dataField="provider" caption="PROVIDER" />
                        <Column dataField="professionalName" caption="PROFESSIONAL NAME" />
                        <Column dataField="contactName" caption="CONTACT NAME" />
                        <Column dataField="contactNo" caption="CONTACT NO" />
                    
                        <Column
                            dataField="services"
                            caption="SERVICES"
                            dataType="string"
                            calculateCellValue={(rowData: IdCheckRow) => getIdCheckServices(rowData).join(", ")}
                        />

                        <Column
                            dataField="lastUpdatedOn"
                            caption="LAST UPDATED"
                            calculateSortValue={(rowData: IdCheckRow) => {
                                return this.gridUtils.convertddmmyyyyStringToDate(rowData.lastUpdatedOn);
                            }}
                        />
                        <Column dataField="lastUpdatedBy" caption="UPDATED BY" />
                    </DataGrid>
                    <LoadPanel shadingColor="rgba(0,0,0,0.4)" visible={this.state.loadIndicatorForGrid} />
                </LightModePageTemplate>
                {this.state.popupVisible ? (
                    <Popup
                        visible={this.state.popupVisible}
                        onHiding={this.hideProfilePopup}
                        dragEnabled={false}
                        hideOnOutsideClick={true}
                        showTitle={true}
                        showCloseButton={true}
                        title="Artist Details"
                        resizeEnabled={true}
                    >
                        <ScrollView width="100%" height="100%">
                            <ArtistProfileComponent
                                mode="view"
                                id={this.state.selectedBusinessEntityId}
                                businessEntityId={this.state.selectedBusinessEntityId}
                                handlePopUpClose={this.handlePopUpClose}
                                handleFeedbackUpdate={() => { return null }}
                                handleAddArtist={() => { return null }}
                                serviceType={"Entertainment"}
                            />
                        </ScrollView>
                    </Popup>
                ) : (null)}
            </>
        );
    }
}

export default IDChecksGrid;
